import { matchPath } from 'react-router-dom'

/**
 * The top-bar rule — round 18, frames `18d` (synthesis) and `18e` (the rule
 * tested against all 28 shell frames).
 *
 *   > No top bar unless the path position is not already named by something
 *   > visible.
 *
 * This replaces `SECTION_LABELS`, an eleven-entry longest-prefix-wins table in
 * `topbar.tsx` that produced three wrong titles and said the same word as the
 * sidebar and the `h1` on a dozen routes.
 *
 * The rule is DERIVED FROM THE ROUTER, not from a prefix table: a route earns a
 * bar when its position in the hierarchy is not already stated by the active
 * sidebar item plus the page `h1`. Three clauses from `18e` refine it:
 *
 *  1. **Master–detail.** `/messages/:eventId` has a path segment but the thread
 *     panel carries the event name beside the list, so a trail above it would
 *     say the same name twice — the exact defect this round is about. No bar.
 *  2. **Guest shell.** `/event/public/:token` has no sidebar to orient from.
 *     It is unaffected here: the token routes are a route group with no
 *     `element:` (`routes/index.tsx`), so no layout and no `Topbar` ever mounts
 *     on them. The rule is scoped to the organiser shell.
 *  3. **Bars carrying side controls** move their controls into the `h1` row
 *     rather than losing them. That is per-page work for the phases that own
 *     those pages; the system controls (theme, notifications, account) are
 *     handled shell-side by `AccountControls`.
 *
 * Below `lg` the bar always renders regardless of this function: it is the
 * mobile header, and `sidebar.tsx` is `hidden lg:flex`, so the rail that would
 * otherwise hold the system controls does not exist there. `18d` decides this
 * explicitly — «Mobil beholder baren uendret».
 */
const TRAIL_ROUTES = [
  '/event/:id',
  '/event/:id/edit',
  '/event/:id/manage',
  '/event/:id/offer/edit',
  '/presentations/:id',
] as const

/** True when this route shows the depth bar at `lg+`. */
export function routeHasTrail(pathname: string): boolean {
  return TRAIL_ROUTES.some((path) => matchPath({ path, end: true }, pathname) !== null)
}

export interface TrailFallback {
  labelKey: string
  to: string
}

/**
 * The parent a nested route returns to, used when the page itself has not
 * published a trail yet (its chunk is still loading, or its record has not
 * resolved). Guarantees the retained bar is never an empty 56px strip — the
 * failure mode `18b` calls out on unmapped routes.
 */
const TRAIL_PARENTS: { path: string; parent: TrailFallback }[] = [
  { path: '/presentations/:id', parent: { labelKey: 'nav.presentations', to: '/presentations' } },
  { path: '/event/:id/*', parent: { labelKey: 'nav.events', to: '/events' } },
  { path: '/event/:id', parent: { labelKey: 'nav.events', to: '/events' } },
]

export function trailParent(pathname: string): TrailFallback | null {
  for (const { path, parent } of TRAIL_PARENTS) {
    if (matchPath({ path, end: true }, pathname)) return parent
  }
  return null
}
